<template>
  <div class="arb-overlay" @click.self="close">
    <div class="arb-modal">
      <div class="arb-close" @click="close">×</div>
      <div class="arb-header">
        <div class="arb-icon">
          <font-awesome-icon :icon="faBolt" />
        </div>
        <div class="arb-title-wrap">
          <h3 class="arb-title">Arb Offers</h3>
          <p class="arb-subtitle">
            Keep your offer posted instead of taking instantly.
          </p>
        </div>
      </div>

      <div class="arb-body">
        <div class="arb-pills">
          <span class="arb-pill">Stay a maker</span>
          <span class="arb-pill">No taker fees</span>
          <span class="arb-pill">Your offer rests on the book</span>
        </div>

        <p class="arb-lead">
          Normally a crossing offer fills instantly and you pay taker fees.
          With Arb Offer on, it rests on the book instead.
        </p>

        <section class="arb-section">
          <h5>Example</h5>
          <div class="book-viz">
            <div class="book-col">
              <div class="book-team">Patriots</div>
              <div class="book-price existing">&minus;140</div>
              <div class="book-label">Existing offer</div>
            </div>
            <div class="book-sep">vs</div>
            <div class="book-col">
              <div class="book-team">Dolphins</div>
              <div class="book-price arb">+140</div>
              <div class="book-label">Your arb offer</div>
            </div>
          </div>

          <div class="arb-compare">
            <div class="arb-compare-row arb-compare-off">
              <div class="arb-compare-label">Without Arb Offer</div>
              <div class="arb-compare-desc">
                Fills right away. You&rsquo;re the <em>taker</em> and pay
                taker fees.
              </div>
            </div>
            <div class="arb-compare-row arb-compare-on">
              <div class="arb-compare-label">With Arb Offer on</div>
              <div class="arb-compare-desc">
                Both sides rest on the book until someone takes them. You
                stay a <em>maker</em>.
              </div>
            </div>
          </div>
        </section>

        <section class="arb-section">
          <h5>How to use it</h5>
          <ol class="arb-steps">
            <li>
              <span class="arb-step-num">1</span>
              <span class="arb-step-text">
                When your offer would fill instantly, you&rsquo;ll see a
                warning.
              </span>
            </li>
            <li>
              <span class="arb-step-num">2</span>
              <span class="arb-step-text">
                Flip <strong>Arb Offer</strong> on &mdash; your offer will
                post instead of taking.
              </span>
            </li>
            <li>
              <span class="arb-step-num">3</span>
              <span class="arb-step-text">
                Turn on <em>Default Arb Offer</em> in Account Settings to
                apply it automatically.
              </span>
            </li>
          </ol>
        </section>
      </div>

      <div class="arb-footer">
        <button
          type="button"
          class="arb-btn arb-btn-secondary"
          :disabled="savingDefault"
          @click="enableByDefault"
        >
          <span v-if="defaultArbOffer">Currently Enabled</span>
          <span v-else-if="savingDefault">Saving…</span>
          <span v-else>Click to Enable by default</span>
        </button>
        <button
          type="button"
          class="arb-btn arb-btn-primary"
          @click="close"
        >
          Got it
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt';
import { useUserStore } from '@/stores/user';
import { useAPI } from '@/composables/useAPI';
import { emitter } from '@/core/emitter';

const emit = defineEmits(['close']);

const userStore = useUserStore();
const savingDefault = ref(false);

const defaultArbOffer = computed(() => !!userStore.user?.defaultArbOffer);

function close() {
  emit('close');
}

async function enableByDefault() {
  if (defaultArbOffer.value || savingDefault.value) {
    close();
    return;
  }
  savingDefault.value = true;

  // Apply the preference locally first so the UI reflects it immediately.
  // The /user/updateDefaultArbOffer endpoint may not exist yet on every
  // environment — we best-effort sync it, but a failure should not block
  // the user from using the feature. localStorage + userStore keep the
  // default applied across sessions either way.
  if (userStore.user) {
    userStore.user.defaultArbOffer = true;
  }
  try {
    localStorage.setItem('defaultArbOffer', 'true');
  } catch (err) {
    // ignore storage errors (private mode / quota)
  }

  try {
    const { request } = useAPI('/user/updateDefaultArbOffer');
    await request({ defaultArbOffer: true });
  } catch (err) {
    // Server sync failed — the local preference is still in effect.
    // eslint-disable-next-line no-console
    console.warn('[ArbOffer] default preference saved locally only:', err);
  }

  emitter.emit('showAlert', {
    type: 'profile-updated',
    shown: true,
    status: 'ok',
    title: 'Arb Offer on by default',
    detail: 'New offers will default to Arb Offer on.',
  });

  savingDefault.value = false;
  close();
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.arb-overlay {
  position: fixed;
  inset: 0;
  z-index: 1052; // one above the base modal wrapper (1051)
  background: main.$black-202;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.arb-modal {
  position: relative;
  background: main.$white_01;
  color: main.$blue-1100;
  border-radius: 15px;
  box-shadow: 0 1px 5px 0 main.$black-100;
  width: 560px;
  max-width: 100%;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
  padding: 1.75rem 1.75rem 1.25rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    padding: 1.25rem 1rem 1rem;
    border-radius: 12px;
  }
}
.arb-close {
  position: absolute;
  top: 0.25rem;
  right: 0.75rem;
  font-size: 2rem;
  line-height: 1;
  color: main.$blue-100;
  cursor: pointer;
  &:hover {
    color: main.$color-primary_01;
  }
}
.arb-header {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1rem;
}
.arb-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: main.$color-primary_01;
  color: main.$white_01;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}
.arb-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}
.arb-title {
  margin: 0;
  font-weight: main.$font-weight-bold;
  font-size: 1.35rem;
  line-height: 1.15;
}
.arb-subtitle {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.35;
  opacity: 0.65;
}
.arb-body {
  margin-top: 0.5rem;
}
.arb-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.9rem;
}
.arb-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.6rem;
  font-size: 0.72rem;
  font-weight: main.$font-weight-semibold;
  letter-spacing: 0.02em;
  color: main.$color-primary_01;
  background: rgba(111, 117, 177, 0.1);
  border: 1px solid rgba(111, 117, 177, 0.22);
  border-radius: 999px;
  white-space: nowrap;
}
.arb-section {
  margin-bottom: 1.4rem;
  &:last-child {
    margin-bottom: 0.5rem;
  }
  h5 {
    margin: 0 0 0.6rem;
    font-size: 0.72rem;
    font-weight: main.$font-weight-bold;
    color: main.$color-primary_01;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
  p {
    margin: 0 0 0.7rem;
    font-size: 0.95rem;
    line-height: 1.55;
    &:last-child {
      margin-bottom: 0;
    }
  }
}
.arb-lead {
  margin: 0 0 1.3rem;
  font-size: 0.83rem;
  line-height: 1.5;
  opacity: 0.65;
}
.book-viz {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 0.5rem;
  background: rgba(111, 117, 177, 0.08);
  border: 1px solid rgba(111, 117, 177, 0.2);
  border-radius: 10px;
  margin-bottom: 0.75rem;
}
.book-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.25rem;
}
.book-team {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: main.$font-weight-semibold;
  opacity: 0.6;
}
.book-price {
  font-size: 1.45rem;
  font-weight: main.$font-weight-bold;
  padding: 0.4rem 0.9rem;
  border-radius: 8px;
  min-width: 92px;
  text-align: center;
  line-height: 1;
  &.existing {
    background: main.$white_01;
    color: main.$blue-1100;
    border: 1px solid main.$grey-500;
  }
  &.arb {
    background: main.$color-primary_01;
    color: main.$white_01;
    box-shadow: 0 0 0 2px rgba(111, 117, 177, 0.25);
  }
}
.book-label {
  font-size: 0.7rem;
  opacity: 0.65;
}
.book-sep {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.45;
  padding: 0 0.2rem;
}
.arb-compare {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.arb-compare-row {
  display: grid;
  grid-template-columns: 9.5rem 1fr;
  align-items: start;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid rgba(111, 117, 177, 0.15);

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
}
.arb-compare-label {
  font-size: 0.78rem;
  font-weight: main.$font-weight-bold;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-top: 0.15rem;
}
.arb-compare-desc {
  font-size: 0.9rem;
  line-height: 1.5;
  em {
    font-style: normal;
    font-weight: main.$font-weight-semibold;
    padding: 0.05rem 0.35rem;
    border-radius: 4px;
  }
}
.arb-compare-off {
  background: rgba(218, 79, 79, 0.05);
  border-color: rgba(218, 79, 79, 0.22);
  .arb-compare-label {
    color: #c14444;
  }
  .arb-compare-desc em {
    background: rgba(218, 79, 79, 0.12);
    color: #a83535;
  }
}
.arb-compare-on {
  background: rgba(76, 164, 109, 0.06);
  border-color: rgba(76, 164, 109, 0.24);
  .arb-compare-label {
    color: #2f8a53;
  }
  .arb-compare-desc em {
    background: rgba(76, 164, 109, 0.15);
    color: #226d3f;
  }
}
.arb-steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
  }
}
.arb-step-num {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: main.$color-primary_01;
  color: main.$white_01;
  font-size: 0.78rem;
  font-weight: main.$font-weight-bold;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  margin-top: 0.05rem;
}
.arb-step-text {
  font-size: 0.93rem;
  line-height: 1.5;
  padding-top: 0.1rem;

  strong {
    font-weight: main.$font-weight-bold;
  }
  em {
    font-style: italic;
    opacity: 0.85;
  }
}
.arb-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(111, 117, 177, 0.15);
  flex-wrap: wrap;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    // Stack full-width on mobile so neither button feels cramped,
    // and put the primary action on the bottom for thumb reach.
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 0.6rem;
    margin-top: 1.25rem;
    padding-top: 0.9rem;

    .arb-btn {
      width: 100%;
      padding: 0.75rem 1rem;
      font-size: 0.95rem;
    }
  }
}
.arb-btn {
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1.1rem;
  font-weight: main.$font-weight-semibold;
  cursor: pointer;
  font-size: 0.9rem;
  transition: transform 0.1s ease, opacity 0.2s ease;
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
.arb-btn-primary {
  background: main.$color-primary_01;
  color: main.$white_01;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
}
.arb-btn-secondary {
  background: transparent;
  color: main.$color-primary_01;
  border: 1px solid main.$color-primary_01;
  &:hover:not(:disabled) {
    background: rgba(111, 117, 177, 0.1);
  }
}
</style>
